import { inject, Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { NeoDialog, NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { DialogOptions, DialogSize } from './dialog.types';

/**
 * Wraps `NeoDialog` with Case Manager defaults and autId conventions for custom dialog
 * components (forms, multi-field flows). Plain confirmations go through `ConfirmationProvider`
 * instead — this provider no longer opens confirmation dialogs.
 * Usage examples live in the `dialog` skill.
 */
@Injectable({
  providedIn: 'root',
})
export class DialogProvider {
  private readonly _neoDialog = inject(NeoDialog);

  /** Opens a custom dialog component. */
  public open<TData = unknown, TResult = unknown, TComponent = unknown>(
    component: ComponentType<TComponent>,
    options?: DialogOptions<TData>,
  ): NeoDialogRef<TComponent, TResult> {
    const size = options?.size || DialogSize.md;
    const neoDialogSize = size as 'sm' | 'md' | 'lg';

    return this._neoDialog.open(component, {
      size: neoDialogSize,
      data: options?.data,
      disableClose: options?.disableClose ?? false,
      hasBackdrop: options?.hasBackdrop ?? true,
      panelClass: options?.panelClass,
      width: options?.width,
    }) as NeoDialogRef<TComponent, TResult>;
  }
}
